import React, { Component }    from 'react';
import PropTypes    from 'prop-types';
import ISO6391      from 'iso-639-1';
import session      from '../../utils/session';
import FinchButton  from './FinchButton';
import validation   from '../../utils/validation';
import urlHandler   from '../../utils/urlHandler';
import settings     from '../../utils/settings';
import permissions  from '../../utils/permissions';
import DatePicker   from 'react-datepicker';
import moment       from 'moment';
import GoogleAnalytics, {categories, actions} from '../../utils/googleAnalytics';
import TranslationLanguages from '../../utils/translationLanguages';

import 'react-datepicker/dist/react-datepicker.css';

import { FilePond } from 'react-filepond';
import 'filepond/dist/filepond.min.css';

const THRESHOLD_POS_DEFAULT = 0.2;
const THRESHOLD_NEG_DEFAULT = 0.6;
const MAX_TAGS_DEFAULT = 3;

const NEG_THRESHOLD_MODELS = [
    {
        name: 'dau',
        defaultValue: 0.6
    }, 
    { 
        name: 'companyDescriptions',
        defaultValue: 0.6
    }, 
    {
        name: 'currentNews',
        defaultValue: 0.6
    }
];

const POS_THRESHOLD_MODELS = [
    {
        name: 'dau',
        defaultValue: 0.2
    }, 
    {
        name: 'dau-2',
        defaultValue: 0.2
    }, 
    {
        name: 'iptc',
        defaultValue: 0.2
    }, 
    {
        name: 'naics',
        defaultValue: 0.2
    }, 
    {
        name: 'companyDescriptions',
        defaultValue: 0.2
    }, 
    {
        name: 'currentNews',
        defaultValue: 0.2
    }, 
    {
        name: 'esg',
        defaultValue: 0.65
    }
];

const MAX_TAGS_MODELS = [
    {
        name: 'dau-2', 
        defaultValue: 3
    }, 
    {
        name: 'iptc', 
        defaultValue: 3
    },
    {
        name: 'esg',
        defaultValue: 3
    }
];

class TextControl extends Component {
    constructor(props) {
        super(props);

        this.submit = this.submit.bind(this);
        this.change = this.change.bind(this);
        this.handleExternalUrlChange = this.handleExternalUrlChange.bind(this);
        this.onChangeLanguageCode = this.onChangeLanguageCode.bind(this);
        this.onChangeSummaryModel = this.onChangeSummaryModel.bind(this);
        this.onChangeTagModel = this.onChangeTagModel.bind(this);
        this.onChangePublishDate = this.onChangePublishDate.bind(this);
        this.onRemoveExtractFile = this.onRemoveExtractFile.bind(this);
        this.onExtractProcessed = this.onExtractProcessed.bind(this);
        this.onExtractError = this.onExtractError.bind(this);
        this.onChangeContext = this.onChangeContext.bind(this);
        this.onThresholdPositiveChange = this.onThresholdPositiveChange.bind(this);
        this.onThresholdNegativeChange = this.onThresholdNegativeChange.bind(this);
        this.onMaxTagsChange = this.onMaxTagsChange.bind(this);
        this.onChangeFormat = this.onChangeFormat.bind(this);
        this.onChangeSourceLanguage = this.onChangeSourceLanguage.bind(this);
        this.onChangeTargetLanguage = this.onChangeTargetLanguage.bind(this);

        this.translationLanguages = new TranslationLanguages(permissions.getPerm('translationLanguages'));

        var translateSourceLanguages = this.translationLanguages.getSourceLanguages();
        var translateSourceLanguage = settings.getValue('translateSourceLanguage', 'auto');
        var translateTargetLanguages = this.translationLanguages.getTargetLanguages(translateSourceLanguage);
        var translateTargetLanguage = settings.getValue('translateTargetLanguage', translateTargetLanguages.length > 0 ? translateTargetLanguages[0].code : 'en');

        this.state = {
            inactive: true,
            submit: false,
            text: '',
            processing: false,
            externalUrl: '',
            externalUrlError: null,
            usingUrl: false,

            languageCode: settings.getValue('languageCode', 'en'),

            summaryModel: settings.getValue('summaryModel', ''),
            summaryModels: settings.getValue('summaryModels', []),

            tagModel: '',
            tagModels: [],
            tagThresholdPositive: THRESHOLD_POS_DEFAULT,
            tagThresholdNegative: THRESHOLD_NEG_DEFAULT,
            tagMaxTags: MAX_TAGS_DEFAULT,

            translateSourceLanguage,
            translateSourceLanguages,
            translateTargetLanguage,
            translateTargetLanguages,
            translateFormat: settings.getValue('translateFormat', 'plain text'),
            translateFormats: ['plain text', 'html']
        };
    }

    componentDidMount() {

        // Get saved tag model
        var tagModel = settings.getValue('tagModel', '');

        // Get list of supported tag models
        var tagModels = settings.getValue('tagModels', []);

        // Set the default selected tag model (if not saved)
        if (tagModel.length == 0 && tagModels.length > 0) {
            tagModel = tagModels[0];
        }

        // Get the default (or saved) max tags value
        var tagMaxTags = settings.getValue(`tagMaxTags_${tagModel}`);
        if (tagMaxTags == null) {
            var mod = MAX_TAGS_MODELS.find((model) => { return model.name == tagModel});
            if (mod) {
                tagMaxTags = mod.defaultValue;
            } else {
                tagMaxTags = MAX_TAGS_DEFAULT;
            }
        }

        // Get the default (or saved) pos threshold value
        var tagThresholdPositive = settings.getValue(`tagPosThreshold_${tagModel}`);
        if (tagThresholdPositive == null) {
            var mod = POS_THRESHOLD_MODELS.find((model) => { return model.name == tagModel});
            if (mod) {
                tagThresholdPositive = mod.defaultValue;
            } else {
                tagThresholdPositive = THRESHOLD_POS_DEFAULT;
            }
        }

        // Get the default (or saved) neg threshold value
        var tagThresholdNegative = settings.getValue(`tagNegThreshold_${tagModel}`);
        if (tagThresholdNegative == null) {
            var mod = NEG_THRESHOLD_MODELS.find((model) => { return model.name == tagModel});
            if (mod) {
                tagThresholdNegative = mod.defaultValue;
            } else {
                tagThresholdNegative = THRESHOLD_NEG_DEFAULT;
            }
        }

        var translateSourceLanguage = settings.getValue('translateSourceLanguage', 'auto');
        var translateTargetLanguages = this.translationLanguages.getTargetLanguages(translateSourceLanguage);
        var translateTargetLanguage = settings.getValue('translateTargetLanguage', translateTargetLanguages.length > 0 ? translateTargetLanguages[0].code : 'en');

        this.setState({
            languageCode: settings.getValue('languageCode', 'en'),
            summaryModel: settings.getValue('summaryModel', ''),
            summaryModels: settings.getValue('summaryModels', []),
            tagModel,
            tagModels,
            tagMaxTags,
            tagThresholdNegative,
            tagThresholdPositive,
            publishDate: settings.getValue('publishDate', undefined) ? moment(settings.getValue('publishDate')) : undefined,
            inactive: !session.isAuthenticated() || (this.props.text === '' && this.state.externalUrl.length == 0) || this.state.usingUrl,
            translateSourceLanguage,
            translateTargetLanguage,
            translateFormat: settings.getValue('translateFormat', 'plain text')
        })
    }

    handleExternalUrlChange(e) {
        this.setState({
            externalUrl: e.target.value,
            externalUrlError: null,
            inactive: false
        });
    }

    submit(e) {
        e.preventDefault();

        // where we need to set MAP: true,
        if (! session.isAuthenticated() || this.state.inactive) {
            return false;
        }

        // Get the entities the user wants to process
        var types = settings.getValue('types', []);
        if (types && types.length) {
            types = types.filter((type) => {
                return type.selected;
            }).map((type) => {
                return type.name;
            });
        }

        this.setState({
            submit: true,
            processing: true,
            noResults: false
        });

        var langCode = 'en';
        if (this.refs.languageCode) {
            langCode = this.refs.languageCode.value;
        }

        var context = null;
        if (this.refs.contextToAdd && this.refs.contextToAdd.value && this.refs.contextToAdd.value.length > 0) {
            context = {
                'text': this.refs.contextToAdd.value
            }
        }

        // See if user wants relationship extractions
        var doReExtraction = settings.getValue('extractRelationships', true);

        if (this.state.externalUrl.length > 0) {
            if (!validation.validHttpUrl(this.state.externalUrl)) {
                this.setState({
                    externalUrlError: 'Invalid URL specified',
                    processing: false,
                    submit: false
                })
            } else {
                var args = `url=${encodeURIComponent(this.state.externalUrl)}`;
                urlHandler.sendRequest('extract-article', null, args, null, null, (response, arg1, http) => {
                    if (http !== 200) {
                        let err = ''
                        if (http === 404) {
                            err = 'We were not able to find this webpage. If you can access the webpage, you can copy and paste the text into the box below.'
                        }
                        else if (http === 500 && 'error_message' in response && response.error_message === 'Failed forward to routing endpoint [7000]') {
                            err = - 'Failed forward to routing endpoint.  Database may be currently updating.'
                        } 
                        else { //(http >= 500 && http < 600) 
                            err = 'We had and issue accessing this webpage. If you can access the webpage, you can copy and paste the text into the box below.'
                        }
                        
                        this.setState({
                            externalUrlError: err,
                            externalUrl: '',
                            processing: false,
                            submit: false
                        })
                    } else {
                        let text = '';
                        let publishDate = undefined;
                        if ('title' in response && response.title && (response.title.length > 0)) {
                            text += response.title + '\n';
                        }
                        if ('authors' in response && response.authors && (response.authors.length > 0)) {
                            text += '\n' + response.authors.join(' ') + '\n';
                        }
                        if ('published' in response && response.published && (response.published.length > 0)) {
                            let d = new Date(Date.parse(response.published));
                            text += '\n' + d.toUTCString() + '\n';
                            publishDate = moment(response.published, 'YYYY-MM-DD');
                        }
                        if ('body' in response && response.body && (response.body.length > 0)) {
                            text += '\n' + response.body + '\n';
                        }             
                                
                        this.refs.textToTag.value = text;
                        if (this.props.onChange) {
                            this.props.onChange(text);
                        }

                        this.setState({
                            externalUrlError: null,
                            externalUrl: '',
                            noResults: false,
                            usingUrl: true,
                            publishDate,
                            processing: true
                        }, () => {
                            var publishDateStr = undefined;
                            if (this.state.publishDate) {
                                publishDateStr = this.state.publishDate.format('YYYY-MM-DD');
                                settings.setValue('publishDate', publishDateStr);
                            }

                            var summaryModel = null;
                            var entitySummarization = this.props.permissions.entitySummarization ? settings.getValue('entitySummarization', true) : false;
                            var documentSummarization = this.props.permissions.documentSummarization ? settings.getValue('documentSummarization', true) : false;
                            if (entitySummarization || documentSummarization) {
                                summaryModel = 'News';
                            }

                            if (this.props.onEnrich) {
                                this.props.onEnrich(
                                    text, 
                                    {
                                        informationExtractionPrompt: settings.getValue('informationExtractionSelected', '-------'),
                                        informationExtraction: settings.getValue('informationExtraction', false) && !!Array.isArray(settings.getValue('informationExtractionTemplates', null)),
                                        sentimentModel: settings.getValue('sentimentModel', window._sentimentModel), 
                                        salienceModel: this.props.permissions.salience ? settings.getValue('salienceModel', 'News') : null,
                                        summaryModel,
                                        lc: langCode,
                                        textCorrections: settings.getValue('textCorrects', []),
                                        gdm: settings.getValue('gdm', 'base'),
                                        types,
                                        publishDate: publishDateStr,
                                        context,
                                        relationships: this.props.permissions.relationships && doReExtraction ? true: false,
                                        coreferences: this.props.permissions.coreferences ? settings.getValue('coreferences', true) : false,
                                        returnTranslation: this.props.permissions.returnTranslation ? true: false,
                                        entitySummarization,
                                        documentSummarization,
                                        sentenceSalience: this.props.permissions.sentenceSalience ? settings.getValue('sentenceSalience', true) : false,
                                        geocodeStreets: this.props.permissions.geocodeStreets ? settings.getValue('geocodeStreets', true) : false,
                                        forceEnglish: settings.getValue('forceEnglish', true)
                                    }
                                );
                                    
                                // Google Analytics
                                var ga = new GoogleAnalytics();
                                ga.event(categories.DEMO, actions.ENTITIES_CLICK);
                            } 
                            else if (this.props.onSentiment) {
                                this.props.onSentiment(
                                    text, 
                                    settings.getValue('sentimentModel', window._sentimentModel));

                                // Google Analytics
                                var ga = new GoogleAnalytics();
                                ga.event(categories.DEMO, actions.SENTIMENT_CLICK);
                            } 
                            else if (this.props.onSummary) {
                                this.props.onSummary(
                                    text, 
                                    this.refs.summaryModel.value,
                                    langCode);
                            } 
                            else if (this.props.onTag) {
                                var tagModel = this.refs.tagModel.value;
                                var maxTags = MAX_TAGS_MODELS.find((mod) => { return mod.name == tagModel }) != null ? this.state.tagMaxTags : null;
                                var posThreshold = POS_THRESHOLD_MODELS.find((mod) => { return mod.name == tagModel }) != null ? this.state.tagThresholdPositive : null;
                                var negThreshold = NEG_THRESHOLD_MODELS.find((mod) => { return mod.name == tagModel }) != null ? this.state.tagThresholdNegative : null;
                                this.props.onTag(
                                    text,
                                    tagModel,
                                    posThreshold,
                                    negThreshold,
                                    maxTags);
                            }
                            else if (this.props.onTranslate) {
                                this.props.onTranslate(
                                    text,
                                    this.state.translateFormat,
                                    this.state.translateTargetLanguage,
                                    this.state.translateSourceLanguage);
                            }
                            else if (this.props.onHeadline) {
                                this.props.onHeadline(
                                    text,
                                    langCode);
                            }
                        });
                    }
                });
            }
        } else {
            var publishDateStr = undefined;
            if (this.state.publishDate) {
                publishDateStr = this.state.publishDate.format('YYYY-MM-DD');
            }

            var summaryModel = null;
            var entitySummarization = this.props.permissions.entitySummarization ? settings.getValue('entitySummarization', true) : false;
            var documentSummarization = this.props.permissions.documentSummarization ? settings.getValue('documentSummarization', true) : false;
            if (entitySummarization || documentSummarization) {
                summaryModel = 'News';
            }

            if (this.props.onEnrich) {
                this.props.onEnrich(
                    this.refs.textToTag.value, 
                    {
                        // should probably add checks that informationExtractionPrompt is in the list of templates otherwise send nothing
                        informationExtractionPrompt: settings.getValue('informationExtractionSelected', '-------'),
                        informationExtraction: settings.getValue('informationExtraction', false) && !!Array.isArray(settings.getValue('informationExtractionTemplates', null)),
                        sentimentModel: settings.getValue('sentimentModel', window._sentimentModel), 
                        salienceModel: this.props.permissions.salience ? settings.getValue('salienceModel', 'News') : null,
                        summaryModel,
                        lc: langCode,
                        textCorrections: settings.getValue('textCorrects', []),
                        gdm: settings.getValue('gdm', 'base'),
                        types,
                        publishDate: publishDateStr,
                        context,
                        relationships: this.props.permissions.relationships && doReExtraction ? true: false,
                        coreferences: this.props.permissions.coreferences ? settings.getValue('coreferences', true) : false,
                        returnTranslation: this.props.permissions.returnTranslation ? true: false,
                        entitySummarization,
                        documentSummarization,
                        sentenceSalience: this.props.permissions.sentenceSalience ? settings.getValue('sentenceSalience', true) : false,
                        geocodeStreets: this.props.permissions.geocodeStreets ? settings.getValue('geocodeStreets', true) : false,
                        forceEnglish: settings.getValue('forceEnglish', true)
                    }
                );

                // Google Analytics
                var ga = new GoogleAnalytics();
                ga.event(categories.DEMO, actions.ENTITIES_CLICK);
            }
            else if (this.props.onSentiment) {
                this.props.onSentiment(
                    this.refs.textToTag.value, 
                    settings.getValue('sentimentModel', window._sentimentModel));

                // Google Analytics
                var ga = new GoogleAnalytics();
                ga.event(categories.DEMO, actions.SENTIMENT_CLICK);
            } 
            else if (this.props.onSummary) {
                this.props.onSummary(
                    this.refs.textToTag.value, 
                    this.refs.summaryModel.value,
                    langCode);
            } 
            else if (this.props.onTag) {
                var tagModel = this.refs.tagModel.value;
                var maxTags = MAX_TAGS_MODELS.find((mod) => { return mod.name == tagModel }) != null ? this.state.tagMaxTags : null;
                var posThreshold = POS_THRESHOLD_MODELS.find((mod) => { return mod.name == tagModel }) != null ? this.state.tagThresholdPositive : null;
                var negThreshold = NEG_THRESHOLD_MODELS.find((mod) => { return mod.name == tagModel }) != null ? this.state.tagThresholdNegative : null;
                this.props.onTag(
                    this.refs.textToTag.value,
                    tagModel,
                    posThreshold,
                    negThreshold,
                    maxTags);
            }
            else if (this.props.onTranslate) {
                console.log('translateTargetLanguage => ', this.state.translateTargetLanguage);
                this.props.onTranslate(
                    this.refs.textToTag.value,
                    this.state.translateFormat,
                    this.state.translateTargetLanguage,
                    this.state.translateSourceLanguage);
            }
            else if (this.props.onHeadline) {
                this.props.onHeadline(
                    this.refs.textToTag.value,
                    langCode);
            }
        }
    }

    change(event) {
        if (event.target.value.length > 0) {
            this.setState({
                inactive: false
            })
        }
        if (this.props.onChange) {
            this.props.onChange(event.target.value);
        }
    }

    onChangeContext(event) {
        if (this.props.onContextChange) {
            this.props.onContextChange(event.target.value);
        }
    }

    onChangeLanguageCode(event) {
        this.setState({
            languageCode: this.refs.languageCode.value
        }, () => {
            settings.setValue('languageCode', this.refs.languageCode.value);
            if (this.refs.languageCode.value != 'en') {
                settings.setValue('textCorrects', []);
            }
            if (this.props.onLanguageChange) {
                this.props.onLanguageChange(this.refs.languageCode.value);
            }
        });
    }

    onChangeSummaryModel(event) {
        this.setState({
            summaryModel: this.refs.summaryModel.value
        }, () => {
            settings.setValue('summaryModel', this.refs.summaryModel.value);
        });
    }

    onChangeTagModel(event) {
        var tagModel = this.refs.tagModel.value;

        // Get the default (or saved) max tags value
        var tagMaxTags = settings.getValue(`tagMaxTags_${tagModel}`);
        if (tagMaxTags == null) {
            var mod = MAX_TAGS_MODELS.find((model) => { return model.name == tagModel});
            if (mod) {
                tagMaxTags = mod.defaultValue;
            } else {
                tagMaxTags = MAX_TAGS_DEFAULT;
            }
        }

        // Get the default (or saved) pos threshold value
        var tagThresholdPositive = settings.getValue(`tagPosThreshold_${tagModel}`);
        if (tagThresholdPositive == null) {
            var mod = POS_THRESHOLD_MODELS.find((model) => { return model.name == tagModel});
            if (mod) {
                tagThresholdPositive = mod.defaultValue;
            } else {
                tagThresholdPositive = THRESHOLD_POS_DEFAULT;
            }
        }

        // Get the default (or saved) neg threshold value
        var tagThresholdNegative = settings.getValue(`tagNegThreshold_${tagModel}`);
        if (tagThresholdNegative == null) {
            var mod = NEG_THRESHOLD_MODELS.find((model) => { return model.name == tagModel});
            if (mod) {
                tagThresholdNegative = mod.defaultValue;
            } else {
                tagThresholdNegative = THRESHOLD_NEG_DEFAULT;
            }
        }

        this.setState({
            tagModel,
            tagMaxTags,
            tagThresholdNegative,
            tagThresholdPositive
        },() => {
            settings.setValue('tagModel', this.refs.tagModel.value);
        })
    }

    onChangePublishDate(date) {
        if (date) {
            var date = moment(date);
            this.setState({
                publishDate: date
            },() => {
                settings.setValue('publishDate', date ? date.format('YYYY-MM-DD') : null);
            })
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.text) {
            this.setState({
                text: nextProps.text,
                inactive: false
            }, () => {
                this.refs.textToTag.value = this.state.text;
            })
        }

        if (nextProps.tikaFile && nextProps.tikaFile !== this.props.tikaFile) {
            this.setState({
                tikaFile: nextProps.tikaFile
            });
        }
    }

    onRemoveExtractFile() {
        this.setState({
            inactive: true
        }, () => {
            this.refs.textToTag.value = '';
            if (this.props.onChange) {
                this.props.onChange('');
            }
        })
    }

    onExtractProcessed(data) {
        this.refs.textToTag.value = data.text;    
        this.setState({
            inactive: false
        },() => {
            if (this.props.onChange) {
                this.props.onChange(data.text);
            }
        });
    }

    onExtractError(data) {
        console.log('Extract Error => ', data); 
    }

    onThresholdPositiveChange(e) {
        var val = parseFloat(e.target.value)
        if (val >= 0 && val <= 1) {
            this.setState({
                tagThresholdPositive: parseFloat(e.target.value)
            }, () => {
                settings.setValue(`tagPosThreshold_${this.state.tagModel}`, this.state.tagThresholdPositive);
            })
        }
    }

    onThresholdNegativeChange(e) {
        var val = parseFloat(e.target.value)
        if (val >= 0 && val <= 1) {
            this.setState({
                tagThresholdNegative: parseFloat(e.target.value)
            }, () => {
                settings.setValue(`tagNegThreshold_${this.state.tagModel}`, this.state.tagThresholdNegative);
            })
        }
    }

    onMaxTagsChange(e) {
        this.setState({
            tagMaxTags: parseInt(e.target.value)
        }, () => {
            settings.setValue(`tagMaxTags_${this.state.tagModel}`, this.state.tagMaxTags);
        })
    }

    onChangeFormat(event) {
        this.setState({
            translateFormat: this.refs.translateFormat.value
        }, () => {
            settings.setValue('translateFormat', this.refs.translateFormat.value);
        });
    }

    onChangeSourceLanguage(event) {
        var targetLngs = this.translationLanguages.getTargetLanguages(this.refs.translateSourceLanguage.value);
        this.setState({
            translateSourceLanguage: this.refs.translateSourceLanguage.value,
            translateTargetLanguage: targetLngs.length > 0 ? targetLngs[0].code : 'en',
            translateTargetLanguages: targetLngs,
        }, () => {
            settings.setValue('translateSourceLanguage', this.refs.translateSourceLanguage.value);
            settings.setValue('translateTargetLanguage', targetLngs.length > 0 ? targetLngs[0].code : 'en');
        });
    }

    onChangeTargetLanguage(event) {
        this.setState({
            translateTargetLanguage: this.refs.translateTargetLanguage.value
        }, () => {
            settings.setValue('translateTargetLanguage', this.refs.translateTargetLanguage.value);
        });
    }

    render() {
        const buttonText = this.props.submitText || 'Show Results';

        return (
            <form>
                {this.props.permissions.convert2 &&
                    <div className="filepond-wrapper">
                        <FilePond ref={(ref) => {this.pond = ref}}
                            onremovefile={() => this.onRemoveExtractFile() }
                            server={{
                                url: '../extract2',
                                timeout: 60000,
                                process: {
                                    onload: (data) => {this.onExtractProcessed(JSON.parse(data))},
                                    onerror: (data) => {this.onExtractError(data)},
                                    headers: {
                                        'X-Finch': 'Bearer ' + session.getToken()
                                    }
                                },
                                restore: null,
                                revert: null,
                                load: null,
                                fetch: null
                            }}
                            name="extractfile"
                            labelIdle={'Drag & Drop your file here, <span class="filepond--label-action\">Browse</span> or paste the text below'}
                            labelFileProcessing={'Extracting text'}  
                            labelFileProcessingComplete={'Extraction complete'}
                            labelFileProcessingError={'Error during extraction'}   
                            labelFileProcessingAborted={'Extraction cancelled'}
                            allowPaste={false}>
                        </FilePond>
                    </div>
                }
                {this.props.permissions.extractArticle &&
                    <div className='externalurl-form form-field-wrapper text-field'>
                        <label htmlFor='externalUrlField' className='form-label'>Enter Url:</label>
                        <input type='text'
                                className='form-field externalurl-form-field'
                                name='externalUrlField'
                                id='externalUrlField'
                                value={ this.state.externalUrl }
                                onChange= { this.handleExternalUrlChange }
                        />
                        { this.state.externalUrlError ?
                            <div className="externalurl-error-msg">
                                { this.state.externalUrlError }
                            </div> 
                            :
                            null
                        }
                    </div>
                }
                 {this.props.permissions.extractArticle &&
                    <div className="form-separator">
                        <label>OR</label>
                    </div>
                }
                <div className="tag-text-form form-field-wrapper">
                    <label className='form-label'>Enter Text:</label>
                    <textarea type='text'
                                className='form-field text-to-tag'
                                defaultValue={ this.props.text}
                                placeholder='Paste news text here'
                                ref='textToTag'
                                onChange={ this.change }/>
                    {this.props.onEnrich &&
                    <div>
                        <label className='form-label'>Additional Context:</label>
                        <textarea type='text'
                                    className=' form-field context-text'
                                    defaultValue={this.props.context ? this.props.context : ''}
                                    ref='contextToAdd'
                                    onChange={this.onChangeContext}/>
                    </div>
                    }
                </div>
                <span>
                    { (this.props.onEnrich || this.props.onSentiment) &&
                        <div>
                            {this.props.supportedLanguages.length > 0 &&
                                <div className='languagecode-form form-field-wrapper text-field'>
                                    <label htmlFor='externalUrlField' className='form-label'>Language:</label>
                                    <select className='form-field languagecode-form-field'
                                            name='languageCodeField'
                                            id='languageCodeField'
                                            ref='languageCode'
                                            value={this.state.languageCode}
                                            onChange={this.onChangeLanguageCode}>
                                        {this.props.supportedLanguages.map((code) => {
                                            return <option key={code} value={code}>{ISO6391.getName(code)}</option>
                                        })}
                                    </select>
                                </div>
                            }
                            {!this.props.onSentiment &&
                                <div className='languagecode-form form-field-wrapper text-field'>
                                    <label className='form-label'>Publish Date:</label>
                                    <DatePicker className='form-field languagecode-form-field' 
                                            ref='publishDate'
                                            selected={this.state.publishDate ? this.state.publishDate.toDate() : undefined}
                                            onChange={this.onChangePublishDate}
                                            placeholderText="Click to select"
                                            dateFormat="yyyy-MM-dd"
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                    />
                                </div>
                            }
                        </div>
                    }
                    { this.props.onSummary &&
                        <div>
                            <div className='summary-form form-field-wrapper text-field'>
                                <label htmlFor='externalUrlField' className='form-label'>Summary Model:</label>
                                <select className='form-field summary-form-field'
                                        name='summaryModelField'
                                        id='summaryModelField'
                                        ref='summaryModel'
                                        value={this.state.summaryModel}
                                        onChange={this.onChangeSummaryModel}>
                                    {this.state.summaryModels.map((model) => {
                                        return <option key={model.trim()} value={model.trim()}>{model}</option>
                                    })}
                                </select>
                            </div>
                            {this.props.supportedLanguages.length > 0 &&
                                <div className='languagecode-form form-field-wrapper text-field'>
                                    <label htmlFor='externalUrlField' className='form-label'>Language:</label>
                                    <select className='form-field languagecode-form-field'
                                            name='languageCodeField'
                                            id='languageCodeField'
                                            ref='languageCode'
                                            value={this.state.languageCode}
                                            onChange={this.onChangeLanguageCode}>
                                        {this.props.supportedLanguages.map((code) => {
                                            return <option key={code} value={code}>{ISO6391.getName(code)}</option>
                                        })}
                                    </select>
                                </div>
                            }
                        </div>
                    }
                    { this.props.onTag && 
                        <div>
                            <div className='tag-form form-field-wrapper text-field'>
                                <label className='form-label'>Tag Model:</label>
                                <select className='form-field tag-form-field'
                                        name='tagModelField'
                                        id='tagModelField'
                                        ref='tagModel'
                                        value={this.state.tagModel}
                                        onChange={this.onChangeTagModel}>
                                    {this.state.tagModels.map((model) => {
                                        return <option key={model.trim()} value={model.trim()}>{model.trim()}</option>
                                    })}
                                </select>
                            </div>
                            {POS_THRESHOLD_MODELS.find((mod) => { return mod.name == this.state.tagModel}) != null &&
                                <div className='tag-form form-field-wrapper text-field'>
                                    <label className='form-label'>Positive Threshold:</label>
                                    <input type='number'
                                        step="0.1"
                                        min="0"
                                        max="1.0"
                                        className='form-field tag-form-field'
                                        value={ this.state.tagThresholdPositive }
                                        onChange= { this.onThresholdPositiveChange }
                                    />
                                </div>
                            }
                            {NEG_THRESHOLD_MODELS.find((mod) => { return mod.name == this.state.tagModel}) != null &&
                                <div className='tag-form form-field-wrapper text-field'>
                                    <label className='form-label'>Negative Threshold:</label>
                                    <input type='number'
                                        step="0.1"
                                        min="0"
                                        max="1.0"
                                        className='form-field tag-form-field'
                                        value={ this.state.tagThresholdNegative }
                                        onChange= { this.onThresholdNegativeChange }
                                    />
                                </div>
                            }
                            {MAX_TAGS_MODELS.find((mod) => { return mod.name == this.state.tagModel}) != null &&
                                <div className='tag-form form-field-wrapper text-field'>
                                    <label className='form-label'>Max Tags:</label>
                                    <input type='number'
                                        step="1"
                                        min="1"
                                        className='form-field tag-form-field'
                                        value={ this.state.tagMaxTags }
                                        onChange= { this.onMaxTagsChange }
                                    />
                                </div>
                            }
                        </div>
                    }
                    { this.props.onTranslate && 
                        <div>
                            {/*
                            <div className='translate-form form-field-wrapper text-field'>
                                <label className='form-label'>Format:</label>
                                <select className='form-field translate-form-field'
                                        name='translateFormatField'
                                        id='translateFormatField'
                                        ref='translateFormat'
                                        defaultValue={this.state.translateFormat}
                                        onChange={this.onChangeFormat}>
                                    {this.state.translateFormats.map((format) => {
                                        return <option key={format.trim()} value={format.trim()}>{format.trim()}</option>
                                    })}
                                </select>
                            </div>
                            */}
                            <div className='translate-form form-field-wrapper text-field'>
                                <label className='form-label'>Source Language:</label>
                                <select className='form-field translate-form-field'
                                        name='translateSourceLanguageField'
                                        id='translateSourceLanguageField'
                                        ref='translateSourceLanguage'
                                        defaultValue={this.state.translateSourceLanguage}
                                        onChange={this.onChangeSourceLanguage}>
                                    {this.state.translateSourceLanguages.map((lang) => {
                                        return <option key={lang.code} value={lang.code}>{lang.name}</option>
                                    })}
                                </select>
                            </div>
                            <div className='translate-form form-field-wrapper text-field'>
                                <label className='form-label'>Target Language:</label>
                                <select className='form-field translate-form-field'
                                        name='translateTargetLanguageField'
                                        id='translateTargetLanguageField'
                                        ref='translateTargetLanguage'
                                        defaultValue={this.state.translateTargetLanguage}
                                        onChange={this.onChangeTargetLanguage}>
                                    {this.state.translateTargetLanguages.map((lang) => {
                                        return <option key={lang.code} value={lang.code}>{lang.name}</option>
                                    })}
                                </select>
                            </div>
                        </div>
                    }
                    {this.props.onHeadline &&
                        <div>
                            {this.props.supportedLanguages.length > 0 &&
                                <div className='languagecode-form form-field-wrapper text-field'>
                                    <label htmlFor='externalUrlField' className='form-label'>Language:</label>
                                    <select className='form-field languagecode-form-field'
                                            name='languageCodeField'
                                            id='languageCodeField'
                                            ref='languageCode'
                                            value={this.state.languageCode}
                                            onChange={this.onChangeLanguageCode}>
                                        {this.props.supportedLanguages.map((code) => {
                                            return <option key={code} value={code}>{ISO6391.getName(code)}</option>
                                        })}
                                    </select>
                                </div>
                            }
                        </div>
                    }
                    <FinchButton buttonClasses="show-results-button" inactive={this.state.inactive} 
                        processing={this.state.processing} primary={true} text={buttonText} submit={this.submit} />
                </span>
                <span className="user-warning" hidden={session.isAuthenticated()}>You must be logged in to use demo.</span>
            </form>
        );
    }
}

TextControl.defaultProps = {
    supportedLanguages: []
}

TextControl.propTypes = {
    text: PropTypes.string,
    context: PropTypes.string,
    submitText: PropTypes.string,
    tikaFile: PropTypes.object,
    onChange: PropTypes.func,
    onEnrich: PropTypes.func,
    onSentiment: PropTypes.func,
    onSummary: PropTypes.func,
    onTag: PropTypes.func,
    onHeadline: PropTypes.func,
    onTikaChange: PropTypes.func,
    permissions: PropTypes.object.isRequired,
    onLanguageChange: PropTypes.func,
    onContextChange: PropTypes.func,
    supportedLanguages: PropTypes.array
};

export default TextControl;
